import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Label } from "@shared/ui/label";
import { Button } from "@shared/ui/button";
import { Checkbox } from "@shared/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { FileText, Plus, X, Eye, Edit } from "lucide-react";
import {
  useCreateNscDocumentTypeConfiguration,
  useGetDocumentType,
  useGetNscResponseTimeConfiguration,
  useUpdateNscDocumentTypeConfiguration,
} from "@features/cx/settings/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PageHeader } from "@shared/components/PageHeader";

interface DocumentType {
  code: string;
  name: string;
  description: string;
  is_active: boolean;
  created_by: string;
  created_date: string;
  child_count: number;
  hierarchy_names: {
    document_type: string;
  };
  hierarchy_codes: {
    document_type: string;
  };
  entity_type_display: string;
}

export interface SelectedDocument {
  code: string;
  name: string;
  required: boolean;
}

export const DocumentTypesConfigurationCard = () => {
  const { data: documentTypesData } = useGetDocumentType("document_type");
  const remoteUtilityId = useRemoteUtilityId();
  const { data: getDocumentTypeConfiguration } =
    useGetNscResponseTimeConfiguration();
  const { mutateAsync: updateDocumentTypeConfiguration } =
    useUpdateNscDocumentTypeConfiguration();
  const { mutateAsync: createDocumentTypeConfiguration } =
    useCreateNscDocumentTypeConfiguration();

  // Find the document type configuration using config_type
  const documentTypeConfig = getDocumentTypeConfiguration?.find(
    (config) => config.configType === "DOCUMENT_TYPE"
  );

  // Initialize with empty array or load from saved configuration
  const [selectedDocuments, setSelectedDocuments] = useState<
    SelectedDocument[]
  >([]);
  const [originalDocuments, setOriginalDocuments] = useState<
    SelectedDocument[]
  >([]); // Store original state for cancel
  const [selectValue, setSelectValue] = useState<string>("");
  const [isEditable, setIsEditable] = useState(
    !documentTypeConfig?.configurationData // editable by default if no config
  );

  // Auto-populate data from API response
  useEffect(() => {
    if (documentTypeConfig?.configurationData && documentTypesData) {
      const configData = documentTypeConfig?.configurationData;

      // Convert configuration data to selectedDocuments array
      // configData format: { "doc_code": required_status, ... } where 0 = not required, 1 = required
      const documentsFromConfig: SelectedDocument[] = Object.entries(
        configData
      ).map(([docCode, requiredStatus]) => {
        const docType = documentTypesData.find(
          (d: DocumentType) => d.code === docCode
        );
        return {
          code: docCode,
          name: docType?.name || docCode,
          required: (requiredStatus as number) === 1, // Convert 1 to true, 0 to false
        };
      });

      setSelectedDocuments(documentsFromConfig);
      setOriginalDocuments(documentsFromConfig); // Save original for cancel
      setIsEditable(false); // disable fields once data is fetched
    } else {
      setIsEditable(true); // editable if no data
    }
  }, [documentTypeConfig, documentTypesData]);

  const handleAddDocument = (docCode: string): void => {
    if (
      !selectedDocuments.find((doc: SelectedDocument) => doc.code === docCode)
    ) {
      const docType = documentTypesData?.find(
        (d: DocumentType) => d.code === docCode
      );
      if (docType) {
        setSelectedDocuments((prev) => [
          ...prev,
          { code: docType.code, name: docType.name, required: false },
        ]);
        setSelectValue(""); // Clear the select after adding
      }
    }
  };

  const handleRemoveDocument = (docCode: string): void => {
    setSelectedDocuments((prev) =>
      prev.filter((doc: SelectedDocument) => doc.code !== docCode)
    );
  };

  const handleDocumentRequiredToggle = (
    docCode: string,
    required: boolean
  ): void => {
    setSelectedDocuments((prev) =>
      prev.map((doc: SelectedDocument) =>
        doc.code === docCode ? { ...doc, required } : doc
      )
    );
  };

  // Filter out already selected document types for the dropdown
  const availableDocumentTypes =
    documentTypesData?.filter(
      (docType: DocumentType) =>
        !selectedDocuments.find((selected) => selected.code === docType.code)
    ) || [];

  const handleFormSubmit = (): void => {
    const operation = documentTypeConfig?.configurationData ? "UPDATE" : "ADD";

    // Transform selectedDocuments array into object with code as key and required status as value (0 = not required, 1 = required)
    const configurationData = selectedDocuments.reduce((acc, doc) => {
      acc[doc.code] = doc.required ? 1 : 0;
      return acc;
    }, {} as Record<string, number>);

    switch (operation) {
      case "UPDATE":
        const payloadEdit = {
          config_type: "DOCUMENT_TYPE",
          remote_utility_id: Number(remoteUtilityId),
          configuration_data: configurationData,
        };
        updateDocumentTypeConfiguration(payloadEdit);
        break;
      case "ADD":
        const payloadAdd = {
          config_type: "DOCUMENT_TYPE",
          remote_utility_id: Number(remoteUtilityId),
          configuration_data: configurationData,
        };
        createDocumentTypeConfiguration(payloadAdd);
        break;
    }
  };

  const handleFormReset = (): void => {
    setSelectedDocuments(originalDocuments); // Restore original documents
    setSelectValue("");
    setIsEditable(false); // Exit edit mode
  };

  return (
    <Card>
        <div className="space-y-6">
          <div className="flex justify-between items-start">
            <PageHeader
              icon={FileText}
              title="Document Types Configuration"
              subtitle="Select which document types should be available in new connection forms and mark them as required or optional."
            />
            {!isEditable && (
              <button
                onClick={() => setIsEditable(true)}
                className="border border-input text-sm px-3 py-1.5 bg-white text-black rounded hover:bg-gray-50 flex items-center gap-1"
              >
                <Edit className="h-4 w-4" />
                Edit
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium text-sm">Selected Document Types</h4>

              <div className="flex gap-2">
                <Select
                  value={selectValue}
                  onValueChange={handleAddDocument}
                  disabled={!isEditable}
                >
                  <SelectTrigger
                    className={`flex-1 ${
                      !isEditable ? "cursor-not-allowed bg-gray-100" : ""
                    }`}
                  >
                    <SelectValue placeholder="Add document type..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {availableDocumentTypes.map((docType: DocumentType) => (
                      <SelectItem
                        key={docType.code}
                        value={docType.code}
                        className="cursor-pointer"
                      >
                        {docType.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                {selectedDocuments.map((doc: SelectedDocument) => {
                  const docInfo = documentTypesData?.find(
                    (d: DocumentType) => d.code === doc.code
                  );
                  return (
                    <div
                      key={doc.code}
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        !isEditable ? "bg-gray-50" : ""
                      }`}
                    >
                      <div className="flex-1">
                        <Label className="text-sm font-medium">
                          {docInfo?.name || doc.name}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {docInfo?.description || "No description available"}
                        </p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          <Label className="text-xs">Required:</Label>
                          <Checkbox
                            checked={doc.required}
                            disabled={!isEditable}
                            onCheckedChange={(checked: boolean) =>
                              handleDocumentRequiredToggle(doc.code, checked)
                            }
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveDocument(doc.code)}
                          className="h-8 w-8"
                          disabled={!isEditable}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {selectedDocuments.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No document types selected.{" "}
                    {isEditable
                      ? "Use the dropdown above to add documents."
                      : "Click Edit to modify selection."}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-sm">
                  Document Selection Summary
                </h4>
              </div>
              <div className="border rounded-lg p-4 bg-muted/50">
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">
                      Required Documents:
                    </Label>
                    <div className="mt-2 space-y-1">
                      {selectedDocuments
                        .filter((doc: SelectedDocument) => doc.required)
                        .map((doc: SelectedDocument) => {
                          const docInfo = documentTypesData?.find(
                            (d: DocumentType) => d.code === doc.code
                          );
                          return (
                            <div
                              key={doc.code}
                              className="flex items-center gap-2"
                            >
                              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                              <span className="text-sm">
                                {docInfo?.name || doc.name}
                              </span>
                            </div>
                          );
                        })}
                      {selectedDocuments.filter(
                        (doc: SelectedDocument) => doc.required
                      ).length === 0 && (
                        <span className="text-sm text-muted-foreground">
                          No required documents selected
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">
                      Optional Documents:
                    </Label>
                    <div className="mt-2 space-y-1">
                      {selectedDocuments
                        .filter((doc: SelectedDocument) => !doc.required)
                        .map((doc: SelectedDocument) => {
                          const docInfo = documentTypesData?.find(
                            (d: DocumentType) => d.code === doc.code
                          );
                          return (
                            <div
                              key={doc.code}
                              className="flex items-center gap-2"
                            >
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span className="text-sm">
                                {docInfo?.name || doc.name}
                              </span>
                            </div>
                          );
                        })}
                      {selectedDocuments.filter(
                        (doc: SelectedDocument) => !doc.required
                      ).length === 0 && (
                        <span className="text-sm text-muted-foreground">
                          No optional documents selected
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground pt-2 border-t">
                    <p>
                      <strong>Total Selected:</strong>{" "}
                      {selectedDocuments.length} document types
                    </p>
                    <p>
                      <strong>Required:</strong>{" "}
                      {selectedDocuments.filter((doc) => doc.required).length}{" "}
                      document types
                    </p>
                    <p>
                      <strong>Optional:</strong>{" "}
                      {selectedDocuments.filter((doc) => !doc.required).length}{" "}
                      document types
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      {isEditable && (
        <CardFooter className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={handleFormReset}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleFormSubmit}>
            Save
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};